import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import * as AOS from 'aos';

import { ButtonComponent } from '@shared/components';
import { CommonMediaQueries, Languages, ROUTES, SectionIds } from '@shared/models';
import { MediaScreenService } from '@shared/services';

import { NavigationService } from '../../services/navigation.service';
import { SocialsComponent } from './socials/socials.component';
import { MarqueeComponent } from './marquee/marquee.component';

@Component({
  selector: 'rp-home-main',
  standalone: true,
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ButtonComponent,
    MarqueeComponent,
    RouterLink,
    SocialsComponent,
    TranslateModule,
  ],
})
export class MainComponent implements OnInit, OnDestroy {
  readonly sectionIds = SectionIds;

  isDesktop$: Observable<boolean>;

  readonly languages = Languages;
  readonly routes = ROUTES;

  private _timeOut: ReturnType<typeof setTimeout>;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _mediaScreenService = inject(MediaScreenService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);

  ngOnInit(): void {
    this.isDesktop$ = this._mediaScreenService.mediaMatcher(CommonMediaQueries.XL);

    this._route.queryParams.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(params => {
      const sectionId = params['section'] as SectionIds;

      if (sectionId) {
        this._timeOut = setTimeout(() => {
          AOS.refresh();
          this.navigateToItem(params['section']);
          this._removeSectionQueryParam();
        }, 50);
      }
    });
  }

  ngOnDestroy(): void {
    if (this._timeOut) {
      clearTimeout(this._timeOut);
    }
  }

  navigateToItem(sectionId: SectionIds): void {
    this._navigationService.navigateToItem(sectionId, true);
  }

  private _removeSectionQueryParam(): void {
    this._router.navigate([], {
      queryParams: { section: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
